import { z } from 'zod';
import { ApiError } from './errors.js';
import { PositiveCents } from './primitives.js';
/**
 * The fulfillment module's vocabulary (`docs/fulfillment-design.md`) — types,
 * the confirm rule, and the arithmetic. No I/O, no SQL, no HTTP.
 *
 * The module sells things somebody else makes: a print-on-demand provider
 * (Printful today) manufactures and ships, and this platform's job is to hold
 * the catalog, take the money, hand over an order and tell the guest where the
 * parcel is. Everything in this file is one of those four sentences reduced to
 * a shape.
 *
 * It lives in `packages/domain` for the reason `payments.ts` gives one file
 * over: **more than one surface needs the same answer and none of them may be
 * the authority for the others.** The catalog sync prices a variant; the guest
 * cart prices the same variant tomorrow; the submission re-checks the number
 * before it goes to the manufacturer. A second implementation of "what does
 * this t-shirt cost" would eventually disagree with the first, and the
 * disagreement would be a guest charged one price and a factory told another.
 *
 * ## The rule this file is really about
 *
 * `confirmOrders`. Decision 2 of the design: an order is submitted as a **draft**
 * unless the tenant has deliberately said otherwise *and* the tenant is live.
 * The reference implementation reached the same posture through an environment
 * variable (`PRINTFUL_DRAFT_ORDERS`), and the failure mode it was guarding
 * against is worth naming: a confirmed Printful order is a **charge on the
 * tenant's card and a garment on a machine**. Getting that wrong in a test
 * environment does not produce a red suite, it produces a parcel.
 */
/* ------------------------------------------------------------------ */
/* The provider                                                        */
/* ------------------------------------------------------------------ */
/**
 * One rail today, named so that a second one is a value rather than a schema
 * change — `TenantPaymentsConfig.provider`'s move, for the same reason.
 */
export const FulfillmentProvider = z.enum(['printful']);
/* ------------------------------------------------------------------ */
/* Prints: a fixed product list, per tenant (decision 4)               */
/* ------------------------------------------------------------------ */
/**
 * One orderable print — "Matte Poster 12×18", at a size, for a price.
 *
 * **Configuration, not catalog**, and that is decision 4 of the design: a print
 * is not a *synced* product at the provider, it is a raw catalog variant plus a
 * file we generate, so there is nothing to sync and nothing the provider knows
 * about it. The tenant's own list is therefore the only statement of what may
 * be ordered, and it lives in the config document beside the credential
 * reference for the same reason the payments block does: it is per-tenant
 * policy, and a table would have bought a second set of RLS policies and two
 * more grants for six scalars.
 *
 * `print_width_px` / `print_height_px` are the **file** dimensions at 150 dpi,
 * not the paper size. They are here rather than derived from the name because
 * the print builder (wave 2) crops to them exactly, and a poster generated at
 * the wrong aspect is a poster with somebody's head cut off.
 */
export const PrintProduct = z.object({
    /** Stable, quoted by a cart line: `poster-12x18`. */
    key: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[a-z0-9][a-z0-9-]*$/, 'must be a lower-case slug'),
    name: z.string().min(1).max(120),
    /** The provider's **catalog** variant id — ordered directly, no sync product. */
    catalog_variant_id: z.number().int().positive(),
    /** Integer cents, always (dev.md §2). The reference list is in dollars; we are not. */
    retail_cents: PositiveCents,
    print_width_px: z.number().int().min(1).max(20_000),
    print_height_px: z.number().int().min(1).max(20_000),
});
/**
 * What a print line carries until the composer turns it into a file (wave 3).
 *
 * Snapshotted at order time from the cart line and the tenant's `PrintProduct`
 * row: the photograph (album + media, which is the storage path), the caption
 * and stamp the guest chose, and the **pixel size** — copied rather than
 * looked up again, so a tenant editing their print list next week does not
 * change the size of a print a guest already paid for. The composer reads
 * this, never the config.
 */
export const PrintSpec = z.object({
    album_id: z.string().min(1).max(128),
    media_id: z.string().min(1).max(128),
    product_key: PrintProduct.shape.key,
    caption: z.string().max(60).default(''),
    stamp: z.boolean().default(false),
    width_px: z.number().int().min(1).max(20_000),
    height_px: z.number().int().min(1).max(20_000),
});
/** A print, as the checkout hands it to the writer — the cart line plus the album it came from. */
export const PrintLineRequest = z.object({
    album_id: z.string().min(1).max(128),
    media_id: z.string().min(1).max(128),
    product_key: PrintProduct.shape.key,
    caption: z.string().max(60).optional(),
    stamp: z.boolean().optional(),
    qty: z.number().int().min(1).max(10),
});
/* ------------------------------------------------------------------ */
/* The tenant's configuration                                          */
/* ------------------------------------------------------------------ */
/**
 * The tenant's fulfillment configuration, as it lives in `tenant_config.config`.
 *
 * **The credential is a reference, not a secret** — decision 2, and exactly the
 * custody discipline `TenantPaymentsConfig.secret_ref`,
 * `webhook_endpoints.secret_ref` and `TWILIO_AUTH_TOKEN_REF` already run. The
 * key itself is never a column, never an env var in production, and never
 * passed to a CLI.
 *
 * `store_id` is optional because a Printful API-type token is already scoped to
 * one store — the reference implementation sends no store header at all. What
 * it buys is the **webhook check**: an inbound event carries a store id, and a
 * tenant that has written theirs down can refuse an event that claims to be
 * about somebody else's shop.
 *
 * `webhook_token` is the unguessable segment of the receiver's URL, and it is a
 * credential for the same reason an album code is: the provider signs nothing
 * (see `apps/core-api/src/routes/fulfillment.ts` for the full, unhappy account),
 * so the secrecy of the path is one of the two gates. Empty means this tenant
 * accepts no inbound events at all, which is the honest default.
 */
export const TenantFulfillmentConfig = z.object({
    provider: FulfillmentProvider.default('printful'),
    /** A Secret Manager resource name. The API key is never here. */
    api_key_ref: z.string().min(1).max(512),
    /** The provider's own id for the store the key is scoped to. Optional; see above. */
    store_id: z.string().max(64).default(''),
    /**
     * **Draft unless deliberately switched on** (decision 2). Never a default,
     * never inferred from an environment, and — see `confirmOrders` — never
     * sufficient on its own.
     */
    confirm: z.boolean().default(false),
    /** The receiver's secret path segment. `''` = this tenant takes no callbacks. */
    webhook_token: z.string().max(128).default(''),
    /** The fixed print list of decision 4. Empty is a merch-only tenant. */
    print_products: z.array(PrintProduct).max(40).default([]),
});
/**
 * Read the fulfillment block out of a config document that may predate it.
 *
 * `paymentsConfigOf`'s shape, for its reason: `tenant_config.config` is *cast*
 * rather than parsed on the hot path, so every reader needs one answer to "this
 * tenant has no fulfillment block at all", and one function is where it lives.
 */
export function fulfillmentConfigOf(config) {
    const raw = config?.fulfillment;
    if (raw === undefined || raw === null)
        return null;
    const parsed = TenantFulfillmentConfig.safeParse(raw);
    return parsed.success ? parsed.data : null;
}
/** Can this tenant actually reach a manufacturer? */
export function fulfillmentUsable(config) {
    return config !== null && config.api_key_ref !== '';
}
/**
 * **Draft or real**, and the answer is an `AND` of two facts that live in
 * different places on purpose (decision 2).
 *
 * `confirm` is the tenant's *intent*: somebody deliberately typed
 * `--confirm` into a CLI that refuses to guess. `tenants.status = 'live'` is
 * the platform's *state*: an onboarding or paused tenant is not a business
 * taking money, whatever their config says.
 *
 * Requiring both is what makes the five-tenant test roster, the fleet seeder,
 * every laptop and every rehearsal environment structurally incapable of
 * putting a garment on a machine — they are not `live`, so the flag cannot
 * reach the wire however it is set. A single flag would have made "did anybody
 * remember to turn it off in dev" the thing standing between a test run and a
 * charge on the owner's card.
 *
 * The argument is the same one `paymentsUsable` makes about `webhook_id`, run
 * the other way round: there, two facts are required before we *may* act; here,
 * two facts are required before we act *irreversibly*.
 */
export function confirmOrders(config, tenantStatus) {
    return config !== null && config.confirm && tenantStatus === 'live';
}
/** The print product with this key, or `null`. One lookup, no second list. */
export function printProductFor(config, key) {
    return config?.print_products.find((p) => p.key === key) ?? null;
}
/* ------------------------------------------------------------------ */
/* Where the parcel goes                                               */
/* ------------------------------------------------------------------ */
/**
 * A shipping recipient, in the provider's own field names.
 *
 * The names are theirs (`state_code`, `country_code`, `zip`) rather than ours,
 * and that is deliberate: this object is *the address on a parcel*, it is
 * assembled once and handed to a manufacturer, and a translation layer between
 * two spellings of "postcode" would be a place for a wrong label to be born.
 * It is the same call `readEvent` in `routes/paypal.ts` makes about PayPal's
 * vocabulary — read their shape honestly, once, at the boundary.
 *
 * `state_code` is optional because most of the world has no state, and
 * `country_code` is not, because a parcel with no country has nowhere to go.
 * The USVI, which is where the first tenant is, is `US` with `state_code: 'VI'`
 * — an ordinary domestic address as far as a carrier is concerned, and the one
 * thing about it worth writing down so nobody "fixes" it into an international.
 */
export const FulfillmentRecipient = z.object({
    name: z.string().min(1).max(120),
    /** Where the provider sends its own shipment notice. Optional; we send ours. */
    email: z.string().email().max(200).optional(),
    phone: z.string().max(40).optional(),
    address1: z.string().min(1).max(200),
    address2: z.string().max(200).optional(),
    city: z.string().min(1).max(120),
    state_code: z.string().max(20).optional(),
    country_code: z
        .string()
        .length(2)
        .regex(/^[A-Z]{2}$/, 'must be an ISO-3166 alpha-2 code'),
    zip: z.string().max(24).optional(),
});
/**
 * A quoted shipping option, in **our** units.
 *
 * The provider quotes `rate` as a decimal string; money is integer cents
 * everywhere in this system (dev.md §2) and the conversion happens once, at the
 * client boundary, exactly as `amountToCents` does for PayPal.
 */
export const ShippingRate = z.object({
    /** The provider's id for the service — `STANDARD`. Sent back on order create. */
    id: z.string().min(1).max(64),
    name: z.string().min(1).max(200),
    rate_cents: PositiveCents,
    min_delivery_days: z.number().int().min(0).max(365).optional(),
    max_delivery_days: z.number().int().min(0).max(365).optional(),
});
/* ------------------------------------------------------------------ */
/* The submission document                                             */
/* ------------------------------------------------------------------ */
/**
 * One line of what we ask the manufacturer to make.
 *
 * **A snapshot, in their vocabulary**, and it is worth being clear that this is
 * not a second copy of `order_lines`. Those rows say what the guest bought and
 * what they were charged; this says what a factory is being asked to
 * manufacture, keyed by the provider's own ids. They are the same purchase
 * expressed twice on purpose, the way `settlement_lines.item_name` is a
 * snapshot rather than a join — and the store refuses to write the two out of
 * step (`placeFulfillmentOrder` checks the arithmetic before it commits).
 *
 * Exactly one of `sync_variant_id` and `catalog_variant_id` carries the order:
 * a **merch** line names a synced store variant, a **print** line names a raw
 * catalog variant plus the file we generated for it. That is the reference
 * implementation's two-shape union, and it is a union because the two are
 * genuinely different requests.
 */
export const FulfillmentItem = z.object({
    /** Merch: the provider's *sync* variant id. Absent on a print line. */
    sync_variant_id: z.string().max(64).optional(),
    /** Prints: the provider's *catalog* variant id. Absent on a merch line. */
    catalog_variant_id: z.number().int().positive().optional(),
    quantity: z.number().int().min(1).max(999),
    /**
     * What the guest paid, per unit. The provider prints it on the packing slip
     * and declares it at customs, so it is the *retail* price and never our cost.
     */
    retail_price_cents: PositiveCents,
    /** For a human reading a submission that failed. One language, snapshotted. */
    name: z.string().max(200).default(''),
    /**
     * Prints only: a URL the **provider** must be able to fetch. Today that is a
     * V4 signed read URL over the private photo bucket, which is why it has a
     * life measured in days and why a submission that sits in the retry queue for
     * a week is a submission that has to be re-prepared rather than re-sent.
     */
    file_url: z.string().max(2048).optional(),
    /**
     * The photograph a print is made from, until `file_url` exists. Present on a
     * print line from the moment it is placed; the composer in `core-api`
     * (`fulfillment/compose.ts`) reads it just before submission and fills
     * `file_url` in. Absent on merch.
     */
    print: PrintSpec.optional(),
});
/**
 * A submission that names neither variant is a request no manufacturer can
 * read, and a submission that names both is two requests wearing one row.
 */
export function assertFulfillmentItem(item, where) {
    const merch = item.sync_variant_id !== undefined && item.sync_variant_id !== '';
    const print = item.catalog_variant_id !== undefined;
    if (merch === print) {
        throw ApiError.invalid(merch
            ? 'A fulfillment line names one variant, not two.'
            : 'A fulfillment line has to name a variant.', { ...where, reason: merch ? 'ambiguous_variant' : 'no_variant' });
    }
    // A print needs either the file itself or the photograph to make one from:
    // the second is what an order carries between checkout and submission, and
    // the composer turns it into the first.
    if (print && (item.file_url === undefined || item.file_url === '') && item.print === undefined) {
        throw ApiError.invalid('A print line needs a file the manufacturer can fetch.', {
            ...where,
            reason: 'no_print_file',
        });
    }
}
/* ------------------------------------------------------------------ */
/* Tracking                                                            */
/* ------------------------------------------------------------------ */
/**
 * The provider's order lifecycle, kept in **their** vocabulary.
 *
 * Not folded onto ours, and this is the same call `PaypalOrderState.status`
 * makes: what the manufacturer says about the parcel is *evidence*, and our
 * `orders.status` is the *conclusion*. Storing their word for it means an
 * operator looking at a stuck order sees the string the provider's own support
 * desk will recognize, rather than our translation of it.
 */
export const FULFILLMENT_PROVIDER_SHIPPED_STATUSES = [
    'fulfilled',
    'partially_fulfilled',
];
export const FulfillmentShipment = z.object({
    carrier: z.string().max(120).default(''),
    tracking_number: z.string().max(120).default(''),
    tracking_url: z.string().max(2048).default(''),
    shipped_at: z.string().max(40).optional(),
});
/**
 * What we know about the parcel, as one document on the order's satellite.
 *
 * JSONB rather than a `fulfillment_shipments` child table, and the distinction
 * `schema/catalog.ts` draws is the one that decides it: a child table is for a
 * value the server must *find* — an operator's "this URL 404s", a reorder, a
 * delete. Nothing addresses a shipment individually. It is read whole with its
 * owning row, replaced whole on every read-back from the provider, and never
 * queried a piece at a time.
 */
export const FulfillmentTracking = z.object({
    /** The provider's word for it — see the constant above. */
    provider_status: z.string().max(64).default(''),
    shipments: z.array(FulfillmentShipment).max(20).default([]),
    /** When we last asked the provider, rather than when they last told us. */
    checked_at: z.string().max(40).optional(),
});
export const EMPTY_TRACKING = { provider_status: '', shipments: [] };
/**
 * Has the parcel left?
 *
 * One function rather than a comparison at each call site, for the reason
 * `orderOutstandingCents` is one function: two spellings of "shipped" would
 * eventually disagree, and the disagreement would be a guest told their parcel
 * is still being printed while they are holding it.
 */
export function providerHasShipped(tracking) {
    if (tracking === null)
        return false;
    return (FULFILLMENT_PROVIDER_SHIPPED_STATUSES.includes(tracking.provider_status) ||
        tracking.shipments.some((s) => s.tracking_number !== ''));
}
/* ------------------------------------------------------------------ */
/* What a caller may ask for                                           */
/* ------------------------------------------------------------------ */
/**
 * One line of a merch basket: **a variant id and a count.**
 *
 * There is no price here and there never may be one — `OrderLineRequest`'s
 * absence, restated for the second kind of basket, because B7 does not get
 * weaker when the thing being sold is a t-shirt. The reference implementation
 * reached the same shape from the other direction: its `resolveCart` re-prices
 * everything server-side from the live catalog, and the client sends
 * identifiers only.
 */
export const FulfillmentLineRequest = z.object({
    /** Our `item_fulfillment_variants.id`. Not the provider's — see the repo. */
    variant_id: z.string().min(1).max(128),
    qty: z.number().int().min(1).max(50),
});
export const FulfillmentBasket = z.array(FulfillmentLineRequest).min(1).max(40);
/**
 * Price a merch basket against our catalog, refusing anything that cannot be
 * made.
 *
 * `priceOrder`'s shape, one lane over, and the same two rules: every refusal
 * **names the thing** (a person is reading it), and the order of the checks is
 * fixed so the message does not depend on how a map iterated.
 *
 * `invalid` (400) is a basket that could never have worked — a variant id that
 * is not this tenant's. `conflict` (409) is one that would have worked a moment
 * ago — a product the owner retired, a variant the provider discontinued —
 * because the agreement's meaning of 409 is "re-check and offer alternatives",
 * which is what a shop should do with a sold-out size and must *not* do with a
 * malformed cart.
 *
 * **Shipping is not in here.** It is quoted live from the provider against a
 * real address (see `ShippingRate`), so it is not a function of the basket
 * alone and pretending otherwise would put a made-up number in the one place
 * the total is computed. The caller adds the quoted rate; the database CHECK on
 * `orders` is what makes sure it was added.
 */
export function priceFulfillmentOrder(catalog, basket, options = {}) {
    const prints = options.prints ?? [];
    if (basket.length === 0 && prints.length === 0) {
        throw ApiError.invalid('An order needs at least one item.', { reason: 'empty_order' });
    }
    const lines = [];
    const seen = new Set();
    for (const [index, requested] of basket.entries()) {
        const where = { line: index, variant_id: requested.variant_id };
        if (seen.has(requested.variant_id)) {
            // Two lines for one variant is a basket that has to be added up before it
            // can be reasoned about; refusing is cheaper than merging silently and
            // then having a guest count three shirts on a two-line receipt.
            throw ApiError.invalid('That variant is on the order twice.', {
                ...where,
                reason: 'duplicate_variant',
            });
        }
        seen.add(requested.variant_id);
        const variant = catalog.get(requested.variant_id);
        if (variant === undefined) {
            throw ApiError.invalid('That is not something this shop sells.', {
                ...where,
                reason: 'unknown_variant',
            });
        }
        if (!variant.active) {
            throw ApiError.conflict(`${variant.name} is no longer on sale.`, {
                ...where,
                reason: 'not_on_sale',
                item_name: variant.name,
            });
        }
        if (!variant.available) {
            throw ApiError.conflict(`${variant.name} is out of stock.`, {
                ...where,
                reason: 'out_of_stock',
                item_name: variant.name,
            });
        }
        const item = {
            sync_variant_id: variant.syncVariantId,
            quantity: requested.qty,
            retail_price_cents: variant.priceCents,
            name: variant.name,
        };
        assertFulfillmentItem(item, where);
        lines.push({
            variantId: variant.id,
            itemId: variant.itemId,
            itemName: variant.name,
            qty: requested.qty,
            unitPriceCents: variant.priceCents,
            lineTotalCents: variant.priceCents * requested.qty,
            item,
        });
    }
    for (const [index, requested] of prints.entries()) {
        const where = {
            line: basket.length + index,
            product_key: requested.product_key,
            media_id: requested.media_id,
        };
        const product = (options.printProducts ?? []).find((p) => p.key === requested.product_key);
        if (product === undefined) {
            throw ApiError.invalid('That print size is not on offer.', {
                ...where,
                reason: 'unknown_print_product',
            });
        }
        const caption = requested.caption ?? '';
        const item = {
            catalog_variant_id: product.catalog_variant_id,
            quantity: requested.qty,
            retail_price_cents: product.retail_cents,
            name: product.name,
            print: {
                album_id: requested.album_id,
                media_id: requested.media_id,
                product_key: product.key,
                caption,
                stamp: requested.stamp ?? false,
                width_px: product.print_width_px,
                height_px: product.print_height_px,
            },
        };
        assertFulfillmentItem(item, where);
        lines.push({
            variantId: null,
            itemId: null,
            // The receipt line: the size, and the caption the guest wrote on it.
            itemName: caption === '' ? product.name : `${product.name} — ${caption}`,
            qty: requested.qty,
            unitPriceCents: product.retail_cents,
            lineTotalCents: product.retail_cents * requested.qty,
            item,
        });
    }
    return {
        lines,
        subtotalCents: lines.reduce((sum, line) => sum + line.lineTotalCents, 0),
    };
}
/**
 * The drift line an operator reads.
 *
 * A function rather than a template at the call site because the CLI, a future
 * console panel and the fleet seeder's report all print it, and three spellings
 * of the same warning is three chances for one of them to print the wrong
 * number on the wrong side of the arrow.
 */
export function formatPriceDrift(drift) {
    const money = (cents) => `$${(cents / 100).toFixed(2)}`;
    return (`price drift: ${drift.variantName} — we charge ${money(drift.ourCents)}, ` +
        `the provider now lists ${money(drift.theirCents)} (kept ours)`);
}
//# sourceMappingURL=fulfillment.js.map